/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UnsavedChangesRequest } from '../../shared/models/internal/modals/unsaved-changes-request';
import { DisplayIdDescriptor } from '../../shared/models/base/display-id-descriptor';

@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesTrackerService {
  private readonly modalRequestSubject = new BehaviorSubject<UnsavedChangesRequest>({} as UnsavedChangesRequest);
  public modalRequest$ = this.modalRequestSubject.asObservable().pipe(
    map((request) => ({
      ...request,
      title: this.getDisplayIdDescriptor()?.extendedDisplayId ?? 'Unsaved changes',
    }))
  );
  private unsavedChangesSubject = new BehaviorSubject<boolean>(false);
  public unsavedChanges$ = this.unsavedChangesSubject.asObservable();

  private readonly UNSAVED_CHANGES_KEY = 'unsavedChanges';

  public saveUnsavedChangesToSessionStorage<T>(key: string, data: T): void {
    sessionStorage.setItem(key, JSON.stringify(data));
    sessionStorage.setItem(this.UNSAVED_CHANGES_KEY, 'true');
  }

  public getUnsavedChangesFromSessionStorage<T>(key: string): T | null {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  public anyUnsavedChanges(): boolean {
    return sessionStorage.getItem(this.UNSAVED_CHANGES_KEY) === 'true';
  }

  /**
   * Checks for any unsaved changes and opens the modal if there are any.
   * @param executeOnLeave Callback function to execute when the user chooses to leave despite unsaved changes.
   * @param executeOnStay Callback function to execute when the user chooses to stay and not lose unsaved changes.
   * @returns A boolean indicating whether there were unsaved changes.
   */
  public areThereAnyUnsavedChanges(executeOnLeave?: () => void, executeOnStay?: () => void): boolean {
    if (this.anyUnsavedChanges()) {
      const requestModal = {
        onLeave: executeOnLeave,
        onStay: executeOnStay,
      } as UnsavedChangesRequest;
      this.showUnsavedChanges(requestModal);
      return true;
    }
    return false;
  }

  public clearUnsavedChangesFromSessionStorage(key: string): void {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(this.UNSAVED_CHANGES_KEY);
  }

  public clearAllUnsavedChanges(): void {
    const keysToRemove = this.getUnsavedKeys();

    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  }

  public triggerUnsavedChangesSave<T>(key: string, data: T): void {
    this.saveUnsavedChangesToSessionStorage(key, data);
    this.unsavedChangesSubject.next(true);
  }

  public showUnsavedChanges(request: UnsavedChangesRequest): void {
    this.modalRequestSubject.next(request);
  }

  private getDisplayIdDescriptor(): DisplayIdDescriptor | null {
    const sessionStorageKeys = this.getUnsavedKeys();
    for (const key of sessionStorageKeys) {
      const data = sessionStorage.getItem(key);
      if (data) {
        const parsedData = JSON.parse(data);
        if (parsedData && parsedData.extendedDisplayId) {
          return parsedData as DisplayIdDescriptor;
        }
      }
    }
    return null;
  }

  private getUnsavedKeys(): string[] {
    const unsavedSessionStorageKeys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('unsaved')) {
        unsavedSessionStorageKeys.push(key);
      }
    }
    return unsavedSessionStorageKeys;
  }
}
